import React, { useMemo, useState, useEffect, useContext } from "react";
import { LoggerContext } from "context/LoggerContext";
import { mapModuleProps } from "../helpers/mapModuleProps";
import { fetchApi } from "../services/APIRequest";
import { useOrganization } from "../context/OrganizationContext";

/**
 * Module component that loads and transforms module data
 * Used for dynamic content sections and handles pagination
 */
const Module = ({ children, module, ordered, orderDirection, moduleRefId, moduleFilter }) => {
	const { logDebug, logError } = useContext(LoggerContext);
	const organization = useOrganization();
	
	// Initialize state
	const [moduleData, setModuleData] = useState([]);
	const [metaData, setMetaData] = useState(null);
	const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load state
	const [currentPage, setCurrentPage] = useState(1);
	
	const refIdPath = moduleRefId ? "/" + moduleRefId : "";

	useEffect(() => {
		// Skip if no module is defined
		if (!module) return;
		
		const loadModule = async () => {
			try {
				// Build query parameters
				const defaultLimit = module.defaultLimit || 10;
				const filters = moduleFilter ? `&${moduleFilter}` : "";
				const orderBy = ordered ? "order_by=sort_number" : "";
				const orderByDirection = orderDirection ? "&order_by_dir=asc" : "&order_by_dir=desc";
				
				// Include pagination for ANY module with hasPagination flag
				const pagingParam = module.hasPagination ? 
					`&page=${currentPage}&limit=${defaultLimit}&paginate=1` : 
					"";
				
				// Log the request details for debugging
				logDebug("Module API request:", { 
					module: module.module_id,
					endpoint: module.endpoint,
					page: currentPage,
					hasPagination: module.hasPagination,
					hasDefaultLimit: !!module.defaultLimit,
					pagingParam 
				});
				
				// Use queryParam syntax correctly: first ? then &
				const queryStart = refIdPath ? "?" : "?";
				const endpoint = `${module.endpoint}/public${refIdPath}${queryStart}${orderBy}${orderByDirection.replace(/^&/, '')}${filters}${pagingParam}`;
				logDebug("Full endpoint:", endpoint);
				
				const response = await fetchApi(endpoint, organization?.otk);
				
				// Process response consistently, handling both paginated and non-paginated formats
				let processedData = [];
				let meta = null;
				
				if (response) {
					// Standard response format: { data: [...], meta: {...} }
					if (response.data && Array.isArray(response.data)) {
						processedData = response.data;
						
						// Extract meta information for pagination
						meta = {
							current_page: response.meta?.current_page || response.current_page || 1,
							last_page: response.meta?.last_page || response.last_page || 1, 
							total: response.meta?.total || response.total || processedData.length,
							per_page: response.meta?.per_page || response.per_page || defaultLimit
						};
						
						logDebug("Received paginated response with meta:", meta);
					} else {
						// Fallback for non-standard response formats
						processedData = Array.isArray(response) ? response : [response];
						logDebug("Received non-paginated data, items:", processedData.length);
					}
					
					// Store metadata for pagination UI
					setMetaData(meta);
					
					 // KRITISCHE VERBESSERUNG: Datenaggregation ohne vollständigen Re-Render
					if (module.hasPagination && currentPage > 1 && processedData.length > 0) {
						// Für Infinite Scroll: Daten anhängen statt ersetzen
						setModuleData(prevData => {
							// Finde Duplikate, um sie zu vermeiden
							const existingIds = new Set(prevData.map(item => item.id || item._id || JSON.stringify(item)));
							const uniqueNewData = processedData.filter(item => 
								!existingIds.has(item.id || item._id || JSON.stringify(item)));
								
							// DOM-Stabilität: Markieren der neuen Elemente für Animation
							const markedNewData = uniqueNewData.map(item => ({
								...item,
								_isNew: true // Markierung für neue Items
							}));
							
							// Kombiniere alte und neue Daten
							return [...prevData, ...markedNewData];
						});
					} else {
						// Standard-Fall: Daten ersetzen
						setModuleData(processedData);
					}
					
					setIsInitialLoad(false);
				} else {
					logError("API returned empty response for module:", module);
					setModuleData([]);
					setIsInitialLoad(false);
				}
			} catch (error) {
				logError("Module fetch error:", error);
				setModuleData([]);
				setIsInitialLoad(false);
			}
		};
		
		// Only load if organization is available
		if (organization?.otk) {
			loadModule();
		}
	}, [module, organization, orderDirection, ordered, refIdPath, moduleFilter, currentPage, logDebug, logError]);

	// Map module data for rendering
	const moduleDataMapped = useMemo(() => {
		if (isInitialLoad) return []; // Don't map during initial load to prevent displaying incomplete data
		
		logDebug(`Module ${module?.module_id}: Mapping data for ${moduleData.length} items`);
		
		const mapped = mapModuleProps(
			module?.module_id, 
			moduleData || [], 
			"", 
			"", 
			{ logDebug, logError }
		);
		
		return mapped;
	}, [moduleData, module, isInitialLoad, logDebug, logError]);

	// Don't render anything if no module is defined
	if (!module) {
		return typeof children === "function" ? children({}) : children;
	}

	// Create pagination props if module has pagination enabled
	const paginationProps = module?.hasPagination ? {
		currentPage,
		setCurrentPage,
		totalPages: metaData?.last_page || Math.ceil((metaData?.total || moduleData.length) / (metaData?.per_page || module.defaultLimit || 10)),
		totalItems: metaData?.total || moduleData.length,
		itemsPerPage: metaData?.per_page || module.defaultLimit || 10,
		hasMultiplePages: (metaData?.last_page > 1) || (moduleData.length >= (module.defaultLimit || 10))
	} : null;

	// When cloning child elements, pass both mapped and raw data
	return (
		<div>
			{typeof children === "function" ? (
				children({ 
					moduleDataMapped, 
					moduleData, 
					paginationProps,
					isLoading: isInitialLoad
				})
			) : (
				React.Children.map(children, (child) =>
					React.cloneElement(child, { 
						moduleDataMapped, 
						moduleData: moduleData || [],
						moduleId: module?.module_id,
						pagination: paginationProps,
						isLoading: isInitialLoad
					})
				)
			)}
		</div>
	);
};

export default Module;
